import { c as _c } from "react/compiler-runtime";
import React from 'react';
import PropTypes from 'prop-types';
import avo from 'analytics/avo';
import useIsBrowser from 'hooks/useIsBrowser';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import usePDPGalleryInteractedEvent from 'data-hooks/useEvents/usePDPGalleryInteractedEvent';
import useProductAR from 'data-hooks/useProductAR';
import { GALLERY_INTERACTION } from 'data-hooks/useEvents/pdpGalleryEventConstants';

// AR Quick Look on iOS/iPadOS Safari; Cylindo's QR flow everywhere else
const supportsARQuickLook = () => {
  const anchor = document.createElement('a');
  return !!anchor.relList?.supports?.('ar');
};
const trackARClick = url => {
  avo.ctaClicked({
    text: 'AR Mode',
    targetUrl: url,
    /* eslint no-underscore-dangle: 0 */
    cta: avo.Cta._3D_VIEWER,
    location: avo.Location.PDP,
    component: avo.Component.PRODUCT_CARD
  });
};

// AR pill for the controls bar; Quick Look URL matches PDPView3DButton
const PDPGalleryRevampARButton = t0 => {
  const $ = _c(34);
  const {
    className,
    showDivider: t1,
    isARActive: t2,
    onLaunchViewerAR: t3,
    onCloseViewerAR: t4
  } = t0;
  const showDivider = t1 === undefined ? false : t1;
  const isARActive = t2 === undefined ? false : t2;
  const onLaunchViewerAR = t3 === undefined ? null : t3;
  const onCloseViewerAR = t4 === undefined ? null : t4;
  const {
    loading,
    data: t5
  } = usePDPBasicDetails();
  let t6;
  if ($[0] !== t5) {
    t6 = t5 === undefined ? {} : t5;
    $[0] = t5;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const basicDetailsData = t6;
  const {
    trackGalleryInteracted
  } = usePDPGalleryInteractedEvent();
  const {
    name: productName,
    custom_properties: t7
  } = basicDetailsData;
  let t8;
  if ($[2] !== t7) {
    t8 = t7 === undefined ? {} : t7;
    $[2] = t7;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const customProperties = t8;
  const arAsset = customProperties.ar_asset;
  const arAssetUpholstery = customProperties.ar_asset_upholstery;
  const {
    data: t9
  } = useProductAR();
  const {
    arPrice
  } = t9;
  const isBrowser = useIsBrowser();
  if (loading || !isBrowser) {
    return null;
  }
  const useQuickLook = !!arAsset && supportsARQuickLook();
  if (!useQuickLook && !onLaunchViewerAR) {
    return null;
  }
  let t10;
  if ($[4] !== showDivider) {
    t10 = showDivider && <div className="h-[45px] w-px bg-gray-light6" />;
    $[4] = showDivider;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  const divider = t10;
  if (!useQuickLook) {
    let t11;
    if ($[6] !== isARActive || $[7] !== onCloseViewerAR || $[8] !== onLaunchViewerAR) {
      t11 = () => {
        if (isARActive) {
          onCloseViewerAR?.();
          return;
        }
        trackARClick("modal");
        onLaunchViewerAR();
      };
      $[6] = isARActive;
      $[7] = onCloseViewerAR;
      $[8] = onLaunchViewerAR;
      $[9] = t11;
    } else {
      t11 = $[9];
    }
    let t12;
    if ($[10] !== className || $[11] !== isARActive || $[12] !== t11) {
      t12 = <button type="button" aria-pressed={isARActive} data-xc="PDPGalleryRevampARModeButton" className={className} onClick={t11}>AR Mode</button>;
      $[10] = className;
      $[11] = isARActive;
      $[12] = t11;
      $[13] = t12;
    } else {
      t12 = $[13];
    }
    let t13;
    if ($[14] !== divider || $[15] !== t12) {
      t13 = <>{divider}{t12}</>;
      $[14] = divider;
      $[15] = t12;
      $[16] = t13;
    } else {
      t13 = $[16];
    }
    return t13;
  }
  const t11 = `#callToAction=Close&checkoutTitle=${encodeURIComponent(productName || "")}`;
  let t12;
  if ($[17] !== arAssetUpholstery || $[18] !== arPrice || $[19] !== t11) {
    t12 = t11.concat(arAssetUpholstery ? `&checkoutSubtitle=${encodeURIComponent(`Shown in ${arAssetUpholstery}`)}` : "", `&price=$${arPrice}`);
    $[17] = arAssetUpholstery;
    $[18] = arPrice;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const banner = t12;
  let t13;
  if ($[21] !== basicDetailsData.attributes || $[22] !== basicDetailsData.attributes_min?.urlKey) {
    t13 = basicDetailsData.attributes_min?.urlKey ?? basicDetailsData.attributes?.find(_temp)?.value;
    $[21] = basicDetailsData.attributes;
    $[22] = basicDetailsData.attributes_min?.urlKey;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const urlKey = t13;
  const canonicalURL = urlKey ? `https://joybird.com${urlKey}` : null;
  const canonicalParam = canonicalURL ? `&canonicalWebPageURL=${encodeURIComponent(canonicalURL)}` : "";
  const url = `${arAsset}${banner}${canonicalParam}&allowsContentScaling=0`;
  let t14;
  if ($[24] !== trackGalleryInteracted || $[25] !== url) {
    t14 = () => {
      trackARClick(url);
      trackGalleryInteracted(GALLERY_INTERACTION.AR_STARTED);
    };
    $[24] = trackGalleryInteracted;
    $[25] = url;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== className || $[28] !== t14 || $[29] !== url) {
    t15 = <a className={className} data-xc="PDPGalleryRevampARModeButton" href={url} onClick={t14}>AR Mode</a>;
    $[27] = className;
    $[28] = t14;
    $[29] = url;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== divider || $[32] !== t15) {
    t16 = <>{divider}{t15}</>;
    $[31] = divider;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  return t16;
};
PDPGalleryRevampARButton.propTypes = {
  className: PropTypes.string,
  showDivider: PropTypes.bool,
  isARActive: PropTypes.bool,
  onLaunchViewerAR: PropTypes.func,
  onCloseViewerAR: PropTypes.func
};
export default PDPGalleryRevampARButton;
function _temp(a) {
  return a.code === "url_key";
}